/**
 * Hook for fetching assessment version history
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { useQuery } from "@tanstack/react-query";
import type { AssessmentHistoryResponse, AssessmentHistoryParams } from "../types";

/**
 * Fetches assessment history for a specific vendor/product
 * @param params - Vendor name and optional product name
 * @returns Assessment history response
 */
export const getAssessmentHistory = async (
  params: AssessmentHistoryParams
): Promise<AssessmentHistoryResponse> => {
  const url = `${APIEndpoints.TPRAAssessments}/${encodeURIComponent(params.vendor_name)}/history`;
  const response = await get<AssessmentHistoryResponse>({
    url,
    config: {
      params: params.product_name ? { product_name: params.product_name } : undefined,
    },
  });
  return response;
};

/**
 * React Query hook for fetching assessment version history
 *
 * @param params - Vendor name and optional product name, or null to disable
 * @returns React Query result with assessment history data
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useAssessmentHistory({
 *   vendor_name: 'Acme Corp',
 *   product_name: 'Widget Pro',
 * });
 * ```
 */
export const useAssessmentHistory = (params: AssessmentHistoryParams | null) => {
  return useQuery({
    queryKey: ['assessment-history', params?.vendor_name, params?.product_name],
    queryFn: async () => {
      if (!params) return null;
      return getAssessmentHistory(params);
    },
    enabled: !!params,
    staleTime: 30000, // 30 seconds
  });
};
